import {type ReactElement, useCallback, useMemo, useRef} from 'react';
import {Link, toast, Typography} from '@ezcater/tapas';
import {Form, Formik, type FormikErrors} from 'formik';
import useTranslation from 'next-translate/useTranslation';
import {twJoin, twMerge} from 'tailwind-merge';

import Loading from '@/components/ui/Loading';
import {
  type ContactUsOrderFragment,
  useContactUsFormQuery,
  useSubmitContactUsFormMutation,
} from '@/graphql/types';
import useTracking from '@/hooks/useTracking';
import {PRIVACY_PATH} from '@/paths';
import {gtm} from '@/utils';
import {contactUsFormSubmitPayload} from '@/utils/googleAnalyticsEvents';
import formatOrderLabel from './formatOrderLabel';
import SelectField from './SelectField';
import SubmitButton from './SubmitButton';
import TextField from './TextField';

type FormValues = {
  fullName: string;
  email: string;
  orderId?: string | null;
  message: string;
};

const formatOrderForSelect = (order: ContactUsOrderFragment) => ({
  label: formatOrderLabel(order),
  value: order.id,
});

type GraphQLError = {
  key: string;
  message: string;
  type: string;
  name: string;
};

type ErrorResponse = {
  graphQLErrors?: GraphQLError[] | null;
};

const extractErrors = ({graphQLErrors}: ErrorResponse): Record<string, string> => {
  if (graphQLErrors == null) return {};

  const errors: Record<string, string> = {};
  graphQLErrors.forEach(({key, message, type, name}) => {
    if (type !== 'field') return;
    errors[key] = `${name} ${message}`;
  });
  return errors;
};

type ContactUsFormSectionProps = {
  onClose?: () => void;
  className?: string;
  variant?: 'modal' | 'page';
};

const ContactUsFormSection = ({
  onClose,
  className = '',
  variant = 'modal',
}: ContactUsFormSectionProps): ReactElement => {
  const {t} = useTranslation('common');
  const {track} = useTracking();
  const {data, loading} = useContactUsFormQuery({partialRefetch: true, errorPolicy: 'all'});
  const [submitContactUs] = useSubmitContactUsFormMutation();
  const recentOrders = data?.me?.consumerAccount?.orders?.nodes;
  const orderOptions = useMemo(() => {
    const options: {label: string; value: string}[] = [{label: '--', value: ''}];

    if (recentOrders) {
      const validOrders = recentOrders.filter(order => order != null);
      options.push(...validOrders.map(formatOrderForSelect));
    }

    return options;
  }, [recentOrders]);

  // Form tracking state
  const formStartedRef = useRef(false);

  const trackFormStarted = useCallback(() => {
    if (!formStartedRef.current) {
      formStartedRef.current = true;
      track('contact-us-form-started', {
        page: window.location.pathname,
        misc_json: JSON.stringify({has_recent_orders: (recentOrders?.length || 0) > 0}),
      });
    }
  }, [track, recentOrders]);

  if (loading) {
    return (
      <div
        className={twJoin(
          'flex items-center justify-center',
          variant === 'page' ? 'min-h-96' : 'w-3/5 bg-gray-100',
        )}
      >
        <Loading />
      </div>
    );
  }

  const validateForm = (values: FormValues) => {
    const errors: FormikErrors<FormValues> = {};
    if (values.fullName === '') errors.fullName = t('components.ContactUs.form.errors.name');
    if (values.email === '') errors.email = t('components.ContactUs.form.errors.email');
    if (values.message === '') errors.message = t('components.ContactUs.form.errors.message');
    return errors;
  };

  const trackGTMEvent = () => {
    gtm.dataLayer(
      contactUsFormSubmitPayload({
        formId: 'contact_us',
        formHost: 'ezcater',
        clickText: t('components.ContactUs.form.sendMessage'),
      }),
    );
  };

  const containerClasses =
    variant === 'page'
      ? 'max-w-2xl mx-auto p-8 bg-white border rounded-2xl'
      : 'bg-gray-100 py-6 pl-4 pr-4 tablet:py-16 tablet:pl-12 tablet:pr-16';

  return (
    <div className={twMerge(containerClasses, className)}>
      {variant === 'page' && (
        <div className="mb-8 text-center">
          <Typography
            render={<h2>{t('components.ContactUs.form.title')}</h2>}
            variant="heading-xl"
            className="mb-2"
          />
          <p className="text-peppercorn-600">{t('components.ContactUs.form.pageDescription')}</p>
        </div>
      )}
      {variant === 'modal' && (
        <p className="mb-6 mt-2 font-bold">{t('components.ContactUs.form.description')}</p>
      )}
      <Formik
        initialValues={{
          fullName: data?.me?.consumerAccount?.fullName ?? '',
          email: data?.me?.consumerAccount?.email ?? '',
          message: '',
          orderId: '',
        }}
        validate={validateForm}
        validateOnChange={false}
        validateOnBlur
        onSubmit={(values, {setErrors, setSubmitting, setTouched}) => {
          const {fullName, email, message, orderId} = values;
          // Mark all fields as touched so validation errors show
          setTouched({
            fullName: true,
            email: true,
            message: true,
          });

          submitContactUs({variables: {fullName, email, message}})
            .then(() => {
              track('submitted-contact-form', {
                page: window.location.pathname,
                order_id: orderId || '',
                misc_json: JSON.stringify({
                  method: 'form',
                  message_length: message.length,
                }),
              });

              toast.success(t('components.ContactUs.modal.confirmation'));
              onClose?.();
              trackGTMEvent();
              setSubmitting(false);
            })
            .catch(response => {
              toast.error(t('components.ContactUs.modal.failure'));
              setErrors(extractErrors(response));
              setSubmitting(false);
            });
        }}
      >
        <Form className="space-y-6">
          <div
            className={twJoin(
              'flex gap-6',
              variant === 'page' ? 'flex-col sm:flex-row' : 'justify-between gap-3',
            )}
          >
            <div className="flex-auto">
              <TextField
                name="fullName"
                label={t('components.ContactUs.form.nameLabel')}
                placeholder={t('components.ContactUs.form.namePlaceholder')}
                onFocus={trackFormStarted}
              />
            </div>
            <div className="flex-auto">
              <TextField
                name="email"
                label={t('components.ContactUs.form.emailLabel')}
                placeholder={t('components.ContactUs.form.emailPlaceholder')}
                onFocus={trackFormStarted}
              />
            </div>
          </div>

          {orderOptions.length > 1 && (
            <div className="flex justify-between">
              <div className="flex-auto">
                <SelectField
                  name="orderId"
                  label={t('components.ContactUs.form.orderNumberLabel')}
                  options={orderOptions}
                  onFocus={trackFormStarted}
                />
              </div>
            </div>
          )}

          <div className="flex justify-between">
            <div className="flex-auto">
              <TextField
                name="message"
                type="textarea"
                label={t('components.ContactUs.form.messageLabel')}
                maxLength={1000}
                className="pb-12"
                placeholder={t('components.ContactUs.form.addMessage')}
                onFocus={trackFormStarted}
              />
            </div>
          </div>

          <div
            className={twJoin(
              'flex items-center justify-between',
              variant === 'page'
                ? 'flex-col-reverse gap-4 border-t pt-4 sm:flex-row sm:gap-0'
                : 'flex-col-reverse gap-2 tablet:flex-row tablet:gap-0',
            )}
          >
            <Link className="text-sm" href={PRIVACY_PATH}>
              {t('components.ContactUs.form.privacyPolicy')}
            </Link>
            <SubmitButton>{t('components.ContactUs.form.sendMessage')}</SubmitButton>
          </div>
        </Form>
      </Formik>
    </div>
  );
};

export default ContactUsFormSection;
